import { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { SEOHead } from '../../components/seo/SEOHead';
import Button from '../../components/base/Button';
import InstallAppCTA from '../../components/base/InstallAppCTA';
import OpenInBrowserButton from '../../components/feature/OpenInBrowserButton';
import { supabase } from '../../lib/supabase';
import toast from '../../utils/toast';
import { logger } from '../../utils/logger';

export default function InstagramEmailConfirmedPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [confirmed, setConfirmed] = useState(false);
  const processedRef = useRef(false);

  useEffect(() => {
    // Proteção contra execução duplicada (React StrictMode)
    if (processedRef.current) {
      logger.debug('[InstagramEmailConfirmedPage] useEffect já executado, ignorando');
      return;
    }
    processedRef.current = true;

    async function handleEmailConfirmation() {
      try {
        logger.info('[InstagramEmailConfirmedPage] Verificando sessão após confirmação de email');
        logger.debug('[InstagramEmailConfirmedPage] URL atual:', window.location.href);
        logger.debug('[InstagramEmailConfirmedPage] Hash:', window.location.hash);
        logger.debug('[InstagramEmailConfirmedPage] Query:', window.location.search);

        // Extrair tokens do hash fragment ou query string
        const hashParams = new URLSearchParams(window.location.hash.substring(1));
        const queryParams = new URLSearchParams(window.location.search);
        
        const accessToken = hashParams.get('access_token') || queryParams.get('access_token');
        const refreshToken = hashParams.get('refresh_token') || queryParams.get('refresh_token');
        const codeParam = queryParams.get('code');

        logger.sensitive('[InstagramEmailConfirmedPage] Parâmetros encontrados:', {
          hasAccessToken: !!accessToken,
          hasRefreshToken: !!refreshToken,
          hasCode: !!codeParam,
        });

        const hasAuthParams = !!accessToken || !!codeParam;
        if (hasAuthParams) {
          logger.debug('[InstagramEmailConfirmedPage] Processando tokens diretamente no cliente global...');
          try {
            if (accessToken && refreshToken) {
              const { data, error } = await supabase.auth.setSession({
                access_token: accessToken,
                refresh_token: refreshToken,
              });
              if (error) throw error;
              if (!data.session) throw new Error('Sessão não retornada pelo servidor');
            } else if (codeParam) {
              const { error } = await (supabase.auth as any).exchangeCodeForSession(codeParam);
              if (error) throw error;
            } else {
              throw new Error('Tokens insuficientes para confirmar sessão');
            }

            try {
              window.history.replaceState({}, document.title, window.location.pathname);
            } catch { /* ignore */ }

            setConfirmed(true);
            toast.success('✅ Email confirmado com sucesso! Bem-vindo ao Magistral!');
          } catch (e) {
            logger.error('[InstagramEmailConfirmedPage] Falha ao processar tokens:', e);
            toast.error('Erro ao confirmar email. Tente novamente.');
            navigate('/instagram/signup');
          }
          return;
        }

        // Aguardar sessão estar disponível
        logger.debug('[InstagramEmailConfirmedPage] Verificando se sessão está disponível...');
        let session = null;
        let tries = 0;
        const maxTries = 10;
        
        while (tries < maxTries) {
          const { data } = await supabase.auth.getSession();
          session = data.session;
          if (session?.user) {
            logger.success(`[InstagramEmailConfirmedPage] Sessão encontrada na tentativa ${tries + 1}`);
            break;
          }
          await new Promise((r) => setTimeout(r, 200));
          tries++;
        }

        if (!session) {
          logger.error(`[InstagramEmailConfirmedPage] Sessão não encontrada após ${maxTries} tentativas`);
          toast.error('Link inválido ou expirado. Solicite um novo email.');
          navigate('/instagram/signup');
          return;
        }

        logger.success('[InstagramEmailConfirmedPage] Email confirmado com sucesso', {
          userId: session.user.id,
          email: session.user.email,
        });

        // Limpar hash/query para evitar reprocessamento
        try {
          window.history.replaceState({}, document.title, window.location.pathname);
        } catch {
          // Ignore history manipulation errors
        }

        setConfirmed(true);
        toast.success('✅ Email confirmado com sucesso! Bem-vindo ao Magistral!');
      } catch (err) {
        logger.error('[InstagramEmailConfirmedPage] Erro inesperado', err);
        toast.error('Erro ao confirmar email. Tente fazer login novamente.');
        navigate('/login');
      } finally {
        setLoading(false);
      }
    }

    void handleEmailConfirmation();
  }, [navigate]);

  const handleContinue = async () => {
    try {
      logger.debug('[InstagramEmailConfirmedPage] Verificando se usuário já tem família...');
      
      // Obter sessão atual
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session?.user?.id) {
        logger.warn('[InstagramEmailConfirmedPage] Sessão não encontrada, redirecionando para login');
        navigate('/login');
        return;
      }

      // Verificar se já existe família criada
      const { data: families, error } = await supabase
        .from('families')
        .select('id')
        .eq('created_by', session.user.id)
        .limit(1);

      if (error) {
        logger.error('[InstagramEmailConfirmedPage] Erro ao verificar família:', error);
        // Em caso de erro, redireciona para setup por segurança
        navigate('/setup-family');
        return;
      }

      // Se já tem família, vai para dashboard; senão, vai para setup
      const hasFamily = families && families.length > 0;
      const destination = hasFamily ? '/dashboard' : '/setup-family';
      
      logger.success(`[InstagramEmailConfirmedPage] Redirecionando para ${destination}`, { hasFamily });
      navigate(destination);
      
    } catch (error) {
      logger.error('[InstagramEmailConfirmedPage] Erro ao processar continuação:', error);
      // Fallback seguro: vai para setup
      navigate('/setup-family');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-green-50 to-white p-6">
        <SEOHead
          title="Confirmando email - Magistral"
          description="Estamos confirmando seu email. Aguarde um instante para continuar."
          robots="noindex, nofollow"
        />

        <div className="text-center">
          <div className="w-16 h-16 border-4 border-green-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <h2 className="text-xl font-bold text-gray-800 mb-2">Confirmando seu email...</h2>
          <p className="text-gray-600 text-sm">Isso leva apenas alguns segundos.</p>
        </div>
      </div>
    );
  }

  if (!confirmed) {
    return null;
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-green-50 to-white p-6">
      <SEOHead
        title="Email confirmado - Magistral"
        description="Seu email foi confirmado com sucesso. Instale o app ou continue pelo navegador."
        robots="noindex, nofollow"
      />

      <div className="max-w-lg w-full">
        <div className="text-center mb-8">
          <div className="w-20 h-20 mx-auto mb-4 rounded-2xl overflow-hidden">
            <img
              src="/Imagens/logo magistral 2.0 verde.png"
              alt="Magistral"
              className="w-full h-full object-cover"
            />
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-xl p-8 mb-6">
          <div className="w-24 h-24 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <i className="ri-check-line text-green-600 text-5xl"></i>
          </div>

          <h1 className="text-3xl font-bold text-gray-800 text-center mb-3">Email confirmado! 🎉</h1>
          <p className="text-center text-gray-600 text-lg mb-8">Bem-vindo ao Magistral!</p>

          <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl p-6 mb-6">
            <div className="flex items-start gap-3 mb-4">
              <div className="w-10 h-10 bg-blue-500 rounded-lg flex items-center justify-center flex-shrink-0">
                <i className="ri-smartphone-line text-white text-xl"></i>
              </div>
              <div>
                <h3 className="font-bold text-gray-800 mb-2">Agora vamos instalar o app!</h3>
                <p className="text-sm text-gray-700">
                  Instale o Magistral no seu celular para usar offline e receber lembretes automáticos.
                </p>
              </div>
            </div>

            <div className="bg-white rounded-lg p-4 mb-4">
              <p className="text-xs font-semibold text-gray-700 mb-3">📱 Como instalar:</p>
              <ul className="space-y-2 text-xs text-gray-600">
                <li className="flex items-start gap-2">
                  <span className="font-bold text-blue-600">Android:</span>
                  <span>Toque nos 3 pontos (⋮) → "Adicionar à tela inicial"</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="font-bold text-blue-600">iPhone:</span>
                  <span>Toque no botão Compartilhar (□↑) → "Adicionar à Tela de Início"</span>
                </li>
              </ul>
            </div>

            <InstallAppCTA />

            <div className="mt-4 w-full">
              <OpenInBrowserButton variant="secondary" />
            </div>
          </div>

          <Button onClick={handleContinue} variant="secondary" className="w-full">
            Ou continuar no navegador
            <i className="ri-arrow-right-line ml-2"></i>
          </Button>
        </div>

        <div className="text-center text-sm text-gray-500">
          <p>✨ Você está pronto para nunca mais esquecer seus remédios!</p>
        </div>
      </div>
    </div>
  );
}
